import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Calendar, Clock, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { BrandLockup } from "@/components/BrandMark";

/** Blog index. */
export function BlogIndex() {
  const [items, setItems] = useState([]);
  useSEO({
    title: "AAC, autism, and gestalt language — the SonSpeak blog",
    description: "Plain-English answers for the searches parents make at 2am. Written by a parent, reviewed for accuracy.",
    path: "/blog",
  });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        setItems(data?.items || []);
      } catch {/* empty list */}
    })();
  }, []);
  return (
    <div className="min-h-screen dotted-bg">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center justify-between bg-[color:var(--cream)]">
        <BrandLockup size={180} />
        <Link to="/register" className="pill-btn primary"><ArrowRight size={14} /> Sign up</Link>
      </header>
      <main className="max-w-4xl mx-auto px-5 sm:px-10 py-12">
        <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3">SonSpeak journal</div>
        <h1 className="display text-4xl sm:text-6xl font-extrabold mb-4">Plain-English AAC writing.</h1>
        <p className="text-lg text-[color:var(--ink-soft)] mb-8">For the searches you actually make at 2am.</p>
        {items.length === 0 ? (
          <div className="brutal-card bg-white p-6 text-[color:var(--ink-soft)]">No posts yet. Check back soon.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="blog-list">
            {items.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="brutal-card bg-white p-5 hover:bg-[color:var(--paper-muted)] transition-colors"
                data-testid={`blog-card-${p.slug}`}
              >
                <div
                  className="w-10 h-10 rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center mb-3 text-xl"
                  style={{ background: p.cover_color || "var(--gold)" }}
                >{p.cover_emoji || "📝"}</div>
                <h2 className="display text-xl font-extrabold leading-tight mb-1">{p.title}</h2>
                {p.subtitle && <p className="text-sm text-[color:var(--ink-soft)] mb-2">{p.subtitle}</p>}
                <div className="text-xs text-[color:var(--ink-soft)] flex items-center gap-3">
                  <span className="flex items-center gap-1"><Clock size={12} /> {p.reading_time_min} min</span>
                  {p.published_at && (
                    <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(p.published_at).toLocaleDateString()}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/** Single post — bare-bones markdown rendering (newlines + headings + bold). */
function renderMarkdown(md) {
  if (!md) return null;
  // Tiny, dependency-free renderer good enough for our content.
  const lines = md.split("\n");
  const out = [];
  let para = [];
  const flush = (key) => {
    if (para.length) {
      out.push(<p key={`p-${key}`} className="leading-relaxed mb-4">{para.join(" ")}</p>);
      para = [];
    }
  };
  lines.forEach((line, i) => {
    if (/^#\s+/.test(line)) { flush(i); out.push(<h2 key={i} className="display text-2xl sm:text-3xl font-extrabold mt-7 mb-3">{line.replace(/^#\s+/, "")}</h2>); }
    else if (/^##\s+/.test(line)) { flush(i); out.push(<h3 key={i} className="display text-xl font-extrabold mt-6 mb-2">{line.replace(/^##\s+/, "")}</h3>); }
    else if (/^\s*[-*]\s+/.test(line)) {
      flush(i); out.push(<li key={i} className="ml-5 list-disc leading-relaxed">{line.replace(/^\s*[-*]\s+/, "")}</li>);
    }
    else if (line.trim() === "") flush(i);
    else para.push(line);
  });
  flush("end");
  return out;
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [slug]);
  useSEO({
    title: post?.title || "SonSpeak journal",
    description: post?.meta_description || post?.subtitle,
    path: `/blog/${slug}`,
  });
  return (
    <div className="min-h-screen dotted-bg">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center justify-between bg-[color:var(--cream)]">
        <Link to="/blog" className="pill-btn"><ArrowLeft size={14} /> All posts</Link>
        <Link to="/register" className="pill-btn primary">Sign up</Link>
      </header>
      <main className="max-w-2xl mx-auto px-5 sm:px-10 py-12">
        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-4 flex gap-2" data-testid="blog-error">
            <AlertCircle size={18} className="shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {post && (
          <article data-testid="blog-post">
            <div
              className="w-12 h-12 rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center mb-4 text-2xl"
              style={{ background: post.cover_color || "var(--gold)" }}
            >{post.cover_emoji || "📝"}</div>
            <h1 className="display text-3xl sm:text-5xl font-extrabold leading-tight mb-3">{post.title}</h1>
            {post.subtitle && <p className="text-lg text-[color:var(--ink-soft)] mb-4">{post.subtitle}</p>}
            <div className="text-xs text-[color:var(--ink-soft)] mb-8 flex items-center gap-3">
              <span className="flex items-center gap-1"><BookOpen size={12} /> {post.author_name}</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {post.reading_time_min} min</span>
              {post.published_at && <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(post.published_at).toLocaleDateString()}</span>}
            </div>
            <div className="text-base sm:text-lg">{renderMarkdown(post.body_md)}</div>
          </article>
        )}
      </main>
    </div>
  );
}
